<template>
  <Teleport to="body">
    <div v-if="visible" class="toolkit-overlay" @click.self="$emit('close')">
      <div class="toolkit-modal">
        <!-- Header -->
        <div class="modal-header">
          <div class="header-title">
            <button
              v-if="selectedRecipe"
              type="button"
              class="back-button"
              @click="selectedRecipe = null"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <svg v-else class="header-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>{{ selectedRecipe ? selectedRecipe.name : 'Atlas Toolkit' }}</span>
          </div>
          <button type="button" class="close-button" @click="$emit('close')">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <!-- Loading State -->
          <div v-if="loading" class="loading-state">
            <div class="loading-spinner"></div>
            <span>Loading recipes...</span>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="error-state">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
              <path d="M12 8V12M12 16H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <span>{{ error }}</span>
            <button type="button" class="retry-button" @click="fetchRecipes">Try Again</button>
          </div>

          <!-- Recipe List -->
          <div v-else-if="!selectedRecipe" class="recipe-list">
            <!-- Search -->
            <div class="search-box">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2"/>
                <path d="M21 21L16.5 16.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search recipes..."
                class="search-input"
              />
            </div>

            <!-- Grouped Recipes -->
            <div v-for="(recipes, category) in groupedRecipes" :key="category" class="recipe-category">
              <div class="category-label">{{ formatCategory(category) }}</div>
              <button
                v-for="recipe in recipes"
                :key="recipe.id"
                type="button"
                class="recipe-card"
                @click="selectRecipe(recipe)"
              >
                <div class="recipe-icon">
                  <component :is="getRecipeIcon(recipe.icon || recipe.category)" />
                </div>
                <div class="recipe-info">
                  <span class="recipe-name">{{ recipe.name }}</span>
                  <span class="recipe-description">{{ recipe.description }}</span>
                </div>
                <svg class="recipe-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>

            <!-- Empty Search -->
            <div v-if="Object.keys(groupedRecipes).length === 0 && searchQuery" class="empty-search">
              <span>No recipes match "{{ searchQuery }}"</span>
            </div>
          </div>

          <!-- Recipe Form -->
          <div v-else class="recipe-form">
            <p class="recipe-form-description">{{ selectedRecipe.description }}</p>

            <div class="form-fields">
              <div
                v-for="param in selectedRecipe.params"
                :key="param.key"
                class="form-field"
              >
                <label :for="'param-' + param.key" class="field-label">
                  {{ param.label }}
                  <span v-if="param.required" class="required-mark">*</span>
                </label>
                <input
                  :id="'param-' + param.key"
                  v-model="formValues[param.key]"
                  :type="getInputType(param.type)"
                  :placeholder="param.placeholder || getPlaceholder(param.type)"
                  class="field-input"
                  :class="{ 'has-error': formErrors[param.key] }"
                />
                <span v-if="formErrors[param.key]" class="field-error">
                  {{ formErrors[param.key] }}
                </span>
              </div>
            </div>

            <div class="form-actions">
              <button
                type="button"
                class="cancel-button"
                @click="selectedRecipe = null"
              >
                Cancel
              </button>
              <button
                type="button"
                class="execute-button"
                :disabled="executing"
                @click="executeRecipe"
              >
                <span v-if="executing" class="button-spinner"></span>
                <span v-else>Run Recipe</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import InsightsService from '@/services/InsightsService';

// Icon components
const DiagnosticsIcon = {
  template: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2"/>
    <path d="M21 21L16.5 16.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    <path d="M11 8V14M8 11H14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
  </svg>`
};

const RefreshIcon = {
  template: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 2V8H15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M3 12C3 7.02944 7.02944 3 12 3C15.3939 3 18.3252 4.94481 19.9999 7.79998" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    <path d="M3 22V16H9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M21 12C21 16.9706 16.9706 21 12 21C8.60615 21 5.67481 19.0552 4.00006 16.2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
  </svg>`
};

const HeartIcon = {
  template: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 12H18L15 21L9 3L6 12H2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`
};

const ChartIcon = {
  template: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 3V21H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M7 14L12 9L16 13L21 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`
};

const TrendDownIcon = {
  template: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M23 18L13.5 8.5L8.5 13.5L1 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M17 18H23V12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`
};

const UsersIcon = {
  template: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="9" cy="7" r="3" stroke="currentColor" stroke-width="2"/>
    <path d="M3 20C3 16.6863 5.68629 14 9 14C12.3137 14 15 16.6863 15 20" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    <circle cx="17" cy="8" r="2.5" stroke="currentColor" stroke-width="2"/>
    <path d="M16 14C18.7614 14 21 16.2386 21 19" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
  </svg>`
};

const FlaskIcon = {
  template: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 3H15M10 3V8.5L4 18C3.5 19 4.2 20 5.5 20H18.5C19.8 20 20.5 19 20 18L14 8.5V3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`
};

const CompareIcon = {
  template: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 3V21M3 12H21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
    <rect x="4" y="4" width="6" height="6" rx="1" stroke="currentColor" stroke-width="2"/>
    <rect x="14" y="14" width="6" height="6" rx="1" stroke="currentColor" stroke-width="2"/>
  </svg>`
};

export default {
  name: 'AtlasToolkitModal',
  components: {
    DiagnosticsIcon,
    RefreshIcon,
    HeartIcon,
    ChartIcon,
    TrendDownIcon,
    UsersIcon,
    FlaskIcon,
    CompareIcon,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      default: null,
    },
  },
  emits: ['close', 'execute'],
  data() {
    return {
      insightsService: new InsightsService(),
      recipes: [],
      loading: false,
      error: null,
      searchQuery: '',
      selectedRecipe: null,
      formValues: {},
      formErrors: {},
      executing: false,
    };
  },
  computed: {
    filteredRecipes() {
      // Ensure recipes is an array
      const recipeList = Array.isArray(this.recipes) ? this.recipes : [];
      if (!this.searchQuery.trim()) {
        return recipeList;
      }
      const query = this.searchQuery.toLowerCase();
      return recipeList.filter(
        (r) =>
          (r.name && r.name.toLowerCase().includes(query)) ||
          (r.description && r.description.toLowerCase().includes(query)) ||
          (r.category && r.category.toLowerCase().includes(query))
      );
    },
    groupedRecipes() {
      const groups = {};
      const recipes = this.filteredRecipes;
      if (!Array.isArray(recipes)) return groups;
      for (const recipe of recipes) {
        const category = recipe.category || 'other';
        if (!groups[category]) {
          groups[category] = [];
        }
        groups[category].push(recipe);
      }
      return groups;
    },
  },
  watch: {
    visible(newVal) {
      if (newVal && (!Array.isArray(this.recipes) || this.recipes.length === 0)) {
        this.fetchRecipes();
      }
      if (!newVal) {
        // Reset state when closing
        this.selectedRecipe = null;
        this.formValues = {};
        this.formErrors = {};
        this.searchQuery = '';
      }
    },
  },
  methods: {
    async fetchRecipes() {
      this.loading = true;
      this.error = null;
      try {
        const response = await this.insightsService.getRecipes();
        // Handle various response formats
        if (Array.isArray(response)) {
          this.recipes = response;
        } else if (response && Array.isArray(response.data)) {
          this.recipes = response.data;
        } else if (response && Array.isArray(response.recipes)) {
          this.recipes = response.recipes;
        } else {
          console.warn('Unexpected recipes response format:', response);
          this.recipes = [];
        }
      } catch (err) {
        this.error = 'Failed to load recipes. Please try again.';
        console.error('Error fetching recipes:', err);
      } finally {
        this.loading = false;
      }
    },
    formatCategory(category) {
      // Convert snake_case or kebab-case to Title Case
      return category
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    getRecipeIcon(iconOrCategory) {
      const iconMap = {
        refresh: 'RefreshIcon',
        heart: 'HeartIcon',
        chart: 'ChartIcon',
        'trend-down': 'TrendDownIcon',
        users: 'UsersIcon',
        flask: 'FlaskIcon',
        compare: 'CompareIcon',
        diagnostics: 'DiagnosticsIcon',
        reports: 'ChartIcon',
        troubleshooting: 'TrendDownIcon',
        attribution: 'FlaskIcon',
      };
      return iconMap[iconOrCategory] || 'DiagnosticsIcon';
    },
    selectRecipe(recipe) {
      this.selectedRecipe = recipe;
      this.formValues = {};
      this.formErrors = {};
      // Initialize form values with empty strings
      for (const param of recipe.params) {
        this.formValues[param.key] = '';
      }
    },
    getInputType(paramType) {
      if (['campaignId', 'customerId', 'attributionId', 'number'].includes(paramType)) {
        return 'number';
      }
      if (paramType === 'date') {
        return 'date';
      }
      return 'text';
    },
    getPlaceholder(paramType) {
      const placeholders = {
        campaignId: 'e.g., 12345',
        customerId: 'e.g., 42',
        attributionId: 'e.g., 15',
        date: 'YYYY-MM-DD',
        number: 'Enter a number',
        string: 'Enter value',
      };
      return placeholders[paramType] || '';
    },
    validateForm() {
      this.formErrors = {};
      let isValid = true;

      for (const param of this.selectedRecipe.params) {
        const value = this.formValues[param.key];

        if (param.required && (!value || String(value).trim() === '')) {
          this.formErrors[param.key] = `${param.label} is required`;
          isValid = false;
          continue;
        }

        if (value && ['campaignId', 'customerId', 'attributionId', 'number'].includes(param.type)) {
          const num = Number(value);
          if (isNaN(num) || num <= 0) {
            this.formErrors[param.key] = 'Must be a positive number';
            isValid = false;
          }
        }
      }

      return isValid;
    },
    async executeRecipe() {
      if (!this.validateForm()) {
        return;
      }

      this.executing = true;

      // Convert numeric fields to numbers
      const params = {};
      for (const param of this.selectedRecipe.params) {
        const value = this.formValues[param.key];
        if (['campaignId', 'customerId', 'attributionId', 'number'].includes(param.type)) {
          params[param.key] = Number(value);
        } else {
          params[param.key] = value;
        }
      }

      this.$emit('execute', {
        recipeId: this.selectedRecipe.id,
        recipeName: this.selectedRecipe.name,
        params,
      });

      // Reset and close will be handled by parent after execution
      this.executing = false;
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
.toolkit-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.toolkit-modal {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
}

.header-icon {
  color: #052f49;
}

.back-button {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: #f3f4f6;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.back-button:hover {
  background: #e5e7eb;
  color: #374151;
}

.close-button {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.close-button:hover {
  background: #f3f4f6;
  color: #111827;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.5rem 1.5rem;
}

/* Loading & Error States */
.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 3rem 1rem;
  color: #6b7280;
  text-align: center;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: #08c1c8;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-state svg {
  color: #ef4444;
}

.retry-button {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #052f49;
  background: #f3f4f6;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}

.retry-button:hover {
  background: #e5e7eb;
}

/* Search Box */
.search-box {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 0.875rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  margin-bottom: 1rem;
}

.search-box svg {
  color: #9ca3af;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 0.875rem;
  color: #111827;
  outline: none;
}

.search-input::placeholder {
  color: #9ca3af;
}

/* Recipe List */
.recipe-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.recipe-category {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.category-label {
  font-size: 0.6875rem;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding-left: 0.25rem;
}

.recipe-card {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.875rem 1rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;
  width: 100%;
}

.recipe-card:hover {
  border-color: #08c1c8;
  background: #f0fdfa;
  transform: translateX(2px);
}

.recipe-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #f3f4f6;
  border-radius: 10px;
  color: #052f49;
  flex-shrink: 0;
}

.recipe-card:hover .recipe-icon {
  background: #ccfbf1;
  color: #0d9488;
}

.recipe-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.recipe-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
}

.recipe-description {
  font-size: 0.75rem;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.recipe-arrow {
  color: #d1d5db;
  flex-shrink: 0;
  transition: all 0.15s;
}

.recipe-card:hover .recipe-arrow {
  color: #08c1c8;
  transform: translateX(2px);
}

.empty-search {
  text-align: center;
  padding: 2rem 1rem;
  color: #9ca3af;
  font-size: 0.875rem;
}

/* Recipe Form */
.recipe-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.recipe-form-description {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
  padding: 0.75rem 1rem;
  background: #f9fafb;
  border-radius: 8px;
  line-height: 1.5;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.field-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
}

.required-mark {
  color: #ef4444;
  margin-left: 0.125rem;
}

.field-input {
  padding: 0.75rem 1rem;
  font-size: 0.9375rem;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  outline: none;
  transition: all 0.15s;
  font-family: inherit;
}

.field-input:focus {
  border-color: #08c1c8;
  box-shadow: 0 0 0 3px rgba(8, 193, 200, 0.1);
}

.field-input.has-error {
  border-color: #ef4444;
}

.field-input.has-error:focus {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.field-error {
  font-size: 0.75rem;
  color: #ef4444;
}

.form-actions {
  display: flex;
  gap: 0.75rem;
  padding-top: 0.5rem;
}

.cancel-button {
  flex: 1;
  padding: 0.75rem 1rem;
  font-size: 0.9375rem;
  font-weight: 500;
  color: #6b7280;
  background: #f3f4f6;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
}

.cancel-button:hover {
  background: #e5e7eb;
  color: #374151;
}

.execute-button {
  flex: 2;
  padding: 0.75rem 1rem;
  font-size: 0.9375rem;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, #052f49 0%, #08c1c8 100%);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.execute-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(5, 47, 73, 0.3);
}

.execute-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.button-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
</style>
